import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import Accordion from "../components/Accordion";
import CampaignSetupForm from "../components/CampaignSetup/CampaignForm/CampaignSetupForm";
import CampaignSetupButton from "../components/CampaignSetup/CampaignSetupButton";
import { Box, Divider } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  campaignNameSelector,
  experienceRangeSelector,
  isSendToAlreadyContactedSelector,
  jobModeSelector,
  jobTitleSelector,
  noticePeriodSelector,
  numberOfSelectedCandidatesSelector,
  salaryRangeSelector,
  selectedFolderSelector,
  selectedJobLocationsSelector,
} from "../../../redux/selectors/campaignSetup.selector";
import { campaignSetupActions } from "../../../redux/slice/campaignSetUpSlice";
import {
  ICampaignSaveAsDraftPayload,
  IErrors,
  IExperienceRange,
  IFolderDetails,
  IJobLocations,
  ISalaryRange,
  TJobMode,
} from "../../../types/campaign.types";
import { useLocation } from "react-router-dom";
import {
  campaignContactableDetailsSelector,
  currencySelector,
  draftDetailsSelector,
  faqDetailsSelector,
  fetchFoldersStatusSelector,
  foldersSelector,
  jobLocationsSelector,
} from "../../../redux/selectors/whatsAppCampaign.selector";
import {
  usePostSaveAndContinueMutation,
  usePostSaveAsDraftMutation,
} from "../../../redux/services/campaignSetupService";
import { ToastContext } from "../../../utils/context/toastContext";
import { MIDDLEWARE_SLUG } from "../../../apis/apiEndPoints";
import { getCurrencySymbol, getDisabledFields, getFormData } from "../../../utils/helpers";
import {
  campaignSenderDetailsSelector,
  templateDetailsSelector,
} from "../../../redux/selectors/campaignCompose.selector";
import { whatsAppCampaignSliceActions } from "../../../redux/slice/whatsAppCampaignSlice";
import { userDataSelector } from "../../../redux/selectors/user.selector";
import {
  getErrorMsg,
  getIsFresher,
  isSeaAndGulf,
  locationsArrayToStr,
  unFormatNumberString,
} from "../../../utils/whatsAppUtils";
import { campaignComposeActions } from "../../../redux/slice/campaignComposeSlice";
import { defaultErrorMsg } from "../../../utils/utils";
import { eventPushFunction } from "@foundit/clevertap";
import { RootState } from "../../../redux/store";

interface IProps {
  isEditMode: boolean;
  stepsCompleted: number;
  stepNumber: number;
  handleEditClick: (step: number) => void;
  handleSaveAndContinueSuccess: () => void;
  initiationId?: string;
  campaignId?: string;
}

const WhatsAppSetup: React.FC<IProps> = ({
  isEditMode,
  stepsCompleted,
  stepNumber,
  handleEditClick,
  handleSaveAndContinueSuccess,
  campaignId,
  initiationId,
}) => {
  const [errors, setErrors] = useState<IErrors>({});

  const location = useLocation();
  const { setToastValue } = useContext(ToastContext);
  const dispatch = useDispatch();
  const { jobTitle } = useSelector(jobTitleSelector);
  const { selectedJobLocations } = useSelector(selectedJobLocationsSelector);
  const { jobMode } = useSelector(jobModeSelector);
  const { experienceRange } = useSelector(experienceRangeSelector);
  const { salaryRange } = useSelector(salaryRangeSelector);
  const { noticePeriod } = useSelector(noticePeriodSelector);
  const { campaignName } = useSelector(campaignNameSelector);
  const { selectedFolder } = useSelector(selectedFolderSelector);
  const { numberOfSelectedCandidates } = useSelector(
    numberOfSelectedCandidatesSelector
  );
  const { isSendToAlreadyContacted } = useSelector(
    isSendToAlreadyContactedSelector
  );
  const { campaignContactableDetails } = useSelector(
    campaignContactableDetailsSelector
  );
  const { jobLocations } = useSelector(jobLocationsSelector);
  const { faqDetails } = useSelector(faqDetailsSelector);
  const { currency } = useSelector(currencySelector);
  const { draftDetails } = useSelector(draftDetailsSelector);
  const { senderDetails } = useSelector(campaignSenderDetailsSelector);
  const { templateDetails } = useSelector(templateDetailsSelector);
  const { userData } = useSelector(userDataSelector);
  const { folders } = useSelector(foldersSelector);
  const { fetchFoldersStatus } = useSelector(fetchFoldersStatusSelector);
  const userDetails = useSelector(
    (state: RootState) => state.campaign.userDetails
  );
  const srpDisabledFields = useSelector((state: RootState) => state.campaign.disabledFields)
  const urlParams = new URLSearchParams(window.location.search);
  const [
    saveAndContinueApi,
    {
      data: saveAndContinueResponse,
      isLoading: isSaveAndContinueLoading,
      isSuccess: isSaveAndContinueSuccess,
      isError: isSaveAndContinueError,
      error: saveAndContinueError,
    },
  ] = usePostSaveAndContinueMutation();
  const [
    saveAsDraftApi,
    {
      isLoading: isSaveAsDraftLoading,
      isSuccess: isSaveAsDraftSuccess,
      isError: isSaveAsDraftError,
      error: saveAdDraftError,
    },
  ] = usePostSaveAsDraftMutation();

  const { triggeredFromPage, isBulkCampaign } = useMemo(() => {
    const queryParams = new URLSearchParams(location?.search);
    return {
      triggeredFromPage: queryParams.get("triggeredFromPage") || "",
      isBulkCampaign:
        queryParams.get("isBulkCampaign") === "true" ? true : false,
    };
  }, [location?.search]);

  const subTitle = useMemo(() => {
    let resultStr = "";
    const locations = selectedJobLocations?.length
      ? locationsArrayToStr(selectedJobLocations)
      : "";

    const title = jobTitle ? `${jobTitle}` : "";

    let experience = "";
    if (getIsFresher(experienceRange?.min)) {
      experience = "Fresher";
    } else {
      experience =
        experienceRange?.min && experienceRange?.max
          ? (experienceRange?.min === "0" && experienceRange.max === "0") ||
            getIsFresher(experienceRange?.min)
            ? "Fresher"
            : `${experienceRange.min?.split(" ")?.[0]}-${
                experienceRange.max?.split(" ")?.[0]
              } years of exp.`
          : "";
    }

    let salary = "";
    if (salaryRange?.min && salaryRange?.max) {
      if (salaryRange?.currency === "INR") {
        const currencySymbol = getCurrencySymbol(salaryRange.currency);
        salary = `${currencySymbol || ""} ${salaryRange.min} - ${
          currencySymbol || ""
        } ${salaryRange.max} (Annual)`;
      } else {
        const currencySymbol = getCurrencySymbol(salaryRange.currency);
        salary = `${currencySymbol || ""} ${salaryRange.min} - ${
          currencySymbol || ""
        } ${salaryRange.max} (Monthly)`;
      }
    }

    if (campaignName && +numberOfSelectedCandidates && title) {
      resultStr += `"${campaignName}" reaching ${numberOfSelectedCandidates} ${
        numberOfSelectedCandidates && +numberOfSelectedCandidates > 1
          ? "candidate"
          : "candidates"
      } for : ${title}`;
    } else if (title) {
      resultStr += `${title}`;
    }

    if (experience) {
      resultStr += `${title ? " | " : ""}${experience}`;
    }

    if (locations) {
      resultStr += `${title || experience ? " | " : ""}${locations}`;
    }

    if (salary) {
      resultStr += `${title || experience || locations ? " | " : ""}${salary}`;
    }

    return resultStr;
  }, [
    campaignName,
    experienceRange.max,
    experienceRange.min,
    jobTitle,
    numberOfSelectedCandidates,
    salaryRange,
    selectedJobLocations,
  ]);

  const isAllFieldValid =
    !!jobTitle &&
    (!!selectedJobLocations?.length || jobMode === "Remote") &&
    !!experienceRange?.min &&
    (!!experienceRange?.max || getIsFresher(experienceRange?.min)) &&
    !!campaignName &&
    !!selectedFolder &&
    !!numberOfSelectedCandidates &&
    !Object.keys(errors)?.length;

  const handleUpdateJobTitle = useCallback(
    (newJobTitle: string) => {
      dispatch(campaignSetupActions.setJobTitle({ jobTitle: newJobTitle }));

      if (errors.jobTitle) {
        const newErrors = { ...errors };
        delete newErrors.jobTitle;
        setErrors(newErrors);
      }
    },
    [dispatch, errors]
  );

  const handleUpdateJobLocations = useCallback(
    (newJobLocations: IJobLocations[]) => {
      dispatch(
        campaignSetupActions.setJobLocations({
          jobLocations: newJobLocations?.filter((item) => !!item),
        })
      );

      if (!newJobLocations?.length) {
        const newErrors = {
          ...errors,
          jobLocations: "Please select job location(s)",
        };
        setErrors(newErrors);
      } else if (errors.jobLocations) {
        const newErrors = { ...errors };
        delete newErrors.jobLocations;
        setErrors(newErrors);
      }
    },
    [dispatch, errors]
  );

  const handleUpdateJobMode = useCallback(
    (newJobMode: string) => {
      dispatch(
        campaignSetupActions.setJobMode({
          jobMode: (newJobMode || "Remote") as TJobMode,
        })
      );

      if (errors.jobLocations && newJobMode === `Remote`) {
        const newErrors = { ...errors };
        delete newErrors.jobLocations;
        setErrors(newErrors);
      }
    },
    [dispatch, errors]
  );

  const handleUpdateExperienceRange = useCallback(
    (newExperienceRange: IExperienceRange) => {
      dispatch(
        campaignSetupActions.setExperienceRange({
          experienceRange: newExperienceRange,
        })
      );

      if (errors.experienceRange) {
        const newErrors = { ...errors };
        delete newErrors.experienceRange;
        setErrors(newErrors);
      }
    },
    [dispatch, errors]
  );

  const handleUpdateSalaryRange = useCallback(
    (newSalaryRange: ISalaryRange) => {
      dispatch(
        campaignSetupActions.setSalaryRange({ salaryRange: newSalaryRange })
      );

      if (
        newSalaryRange?.min &&
        newSalaryRange?.max &&
        +unFormatNumberString(newSalaryRange?.min) <=
          +unFormatNumberString(newSalaryRange?.max)
      ) {
        setErrors((prevErrors) => {
          const newErrors = { ...prevErrors };
          delete newErrors.salaryRange;
          return newErrors;
        });
      }
    },
    [dispatch]
  );

  const handleBlur = () => {
    if (
      salaryRange?.min &&
      salaryRange?.max &&
      +unFormatNumberString(salaryRange?.min) >
        +unFormatNumberString(salaryRange?.max)
    ) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        newErrors.salaryRange = defaultErrorMsg.salaryRange;
        return newErrors;
      });
    } else {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors.salaryRange;
        return newErrors;
      });
    }
  };

  const handleUpdateNoticePeriod = useCallback(
    (newNoticePeriod: string) => {
      dispatch(
        campaignSetupActions.setNoticePeriod({ noticePeriod: newNoticePeriod })
      );
    },
    [dispatch]
  );

  const handleUpdateCampaignName = useCallback(
    (newCampaignName: string) => {
      dispatch(
        campaignSetupActions.setCampaignName({ campaignName: newCampaignName })
      );

      if (errors.campaignName) {
        const newErrors = { ...errors };
        delete newErrors.campaignName;
        setErrors(newErrors);
      }
    },
    [dispatch, errors]
  );

  const handleUpdateSelectedFolder = useCallback(
    (newSelectedFolder: IFolderDetails | null) => {
      dispatch(
        campaignSetupActions.setSelectedFolder({
          selectedFolder: newSelectedFolder || null,
        })
      );

      if (errors.saveInFolder) {
        const newErrors = { ...errors };
        delete newErrors.saveInFolder;
        setErrors(newErrors);
      }
    },
    [dispatch, errors]
  );

  const handleUpdateNumberOfSelectedCandidates = useCallback(
    (newNumberOfSelectedCandidates: string) => {
      dispatch(
        campaignSetupActions.setNumberOfSelectedCandidates({
          numberOfSelectedCandidates: newNumberOfSelectedCandidates,
        })
      );

      if (
        +newNumberOfSelectedCandidates > campaignContactableDetails.profileCount
      ) {
        setErrors((prev) => ({
          ...prev,
          selectCandidates: "The value entered is more than the max limit",
        }));
      } else if (
        newNumberOfSelectedCandidates &&
        +newNumberOfSelectedCandidates < 1
      ) {
        setErrors((prev) => ({
          ...prev,
          selectCandidates: `Count of profile should be greater than 0 and less than or equal to ${campaignContactableDetails?.profileCount}.`,
        }));
      } else if (errors.selectCandidates) {
        const newErrors = { ...errors };
        delete newErrors.selectCandidates;
        setErrors(newErrors);
      }
    },
    [campaignContactableDetails?.profileCount, dispatch, errors]
  );

  const handleUpdateIsSendToAlreadyContacted = useCallback(
    (newIsSendToAlreadyContacted: boolean) => {
      dispatch(
        campaignSetupActions.setIsSendToAlreadyContacted({
          isSendToAlreadyContacted: newIsSendToAlreadyContacted,
        })
      );
    },
    [dispatch]
  );

  const getCampaignId = useCallback(() => {
    if (campaignId) {
      return +campaignId;
    }
    if (saveAndContinueResponse?.id) {
      return +saveAndContinueResponse.id;
    }

    return null;
  }, [campaignId, saveAndContinueResponse?.id]);

  const handleSaveAndContinue = useCallback(() => {
    const errorsMap: IErrors = {};
    if (!jobTitle) {
      errorsMap.jobTitle = "Please enter job title";
    }

    if (!selectedJobLocations?.length && jobMode?.toLowerCase() !== "remote") {
      errorsMap.jobLocations = "Please select job location(s)";
    }

    if (
      (getIsFresher(experienceRange?.min) && !experienceRange?.min) ||
      (!getIsFresher(experienceRange?.min) &&
        (!experienceRange?.min || !experienceRange?.max))
    ) {
      errorsMap.experienceRange = "Please select min and max experience";
    }

    if (!campaignName) {
      errorsMap.campaignName = "Please enter campaign name";
    }

    if (!selectedFolder) {
      errorsMap.saveInFolder = "Please select or create a folder";
    }

    if (
      (!salaryRange?.min && salaryRange?.max) ||
      (!salaryRange?.max && salaryRange?.min)
    ) {
      errorsMap.salaryRange = "Please add complete salary range";
    } else if (
      salaryRange?.min &&
      salaryRange?.max &&
      +unFormatNumberString(salaryRange?.min) >
        +unFormatNumberString(salaryRange?.max)
    ) {
      errorsMap.salaryRange = defaultErrorMsg.salaryRange;
    }

    if (
      !numberOfSelectedCandidates ||
      (numberOfSelectedCandidates && +numberOfSelectedCandidates < 1) ||
      (numberOfSelectedCandidates &&
        +numberOfSelectedCandidates > campaignContactableDetails?.profileCount)
    ) {
      errorsMap.selectCandidates = `Count of profile should be greater than 0 and less than or equal to ${campaignContactableDetails?.profileCount}.`;
    }

    if (Object.keys(errorsMap)?.length) {
      setErrors(errorsMap);
    } else {
      const payload: ICampaignSaveAsDraftPayload = {
        campaignName,
        folderId: selectedFolder?.folderId || -1,
        folderName: selectedFolder?.name || "",
        initiationId: initiationId ? +initiationId : -1,
        jobDescription: {
          jobTitle,
          currencyCode: salaryRange.currency,
          jobLocations:
            jobMode === "Remote"
              ? []
              : selectedJobLocations?.map((item) => item.name) || [],
          jobMode,
          maxExp: getIsFresher(experienceRange.min)
            ? "1 year"
            : `${experienceRange.max}`,
          minExp: `${experienceRange.min}`,
          maxSalary: salaryRange.max,
          minSalary: salaryRange.min,
          noticePeriod: noticePeriod || "",
        },
        recruiterDetail: null,
        selectedCount: +numberOfSelectedCandidates,
        sendMailToContactedProfiles: isSendToAlreadyContacted ? 1 : 0,
        status: "DRAFT",
        campaignId: getCampaignId(),
        whatsAppCountryCode: senderDetails?.countryCode,
        whatsAppName: senderDetails?.name,
        whatsAppNumber: senderDetails?.number,
        whatsAppNumberId: senderDetails?.id,
        templateId: templateDetails?.selectedTemplate?.id || null,
      };

      const formData = getFormData(payload);

      saveAndContinueApi({
        payload: formData,
      });

      dispatch(
        campaignSetupActions.setIsSalaryRangeFilledInSetup({
          value: !!salaryRange.max && !!salaryRange.min,
        })
      );

      // reset cache
      dispatch(campaignComposeActions.resetTemplatesPlaceholderCache());

      // reset selected template
      dispatch(
        campaignComposeActions.setSelectedTemplate({
          selectedTemplate: null,
        })
      );
    }
  }, [
    jobTitle,
    selectedJobLocations,
    jobMode,
    experienceRange.min,
    experienceRange.max,
    campaignName,
    selectedFolder,
    numberOfSelectedCandidates,
    campaignContactableDetails?.profileCount,
    initiationId,
    salaryRange.currency,
    salaryRange.max,
    salaryRange.min,
    noticePeriod,
    isSendToAlreadyContacted,
    getCampaignId,
    senderDetails?.countryCode,
    senderDetails?.name,
    senderDetails?.number,
    senderDetails?.id,
    templateDetails?.selectedTemplate?.id,
    saveAndContinueApi,
    dispatch,
  ]);

  const handleSaveAsDraft = useCallback(() => {
    // save as draft
    const disabledFields = getDisabledFields(srpDisabledFields)
    const payload: ICampaignSaveAsDraftPayload = {
      campaignName,
      folderId: selectedFolder?.folderId || -1,
      folderName: selectedFolder?.name || "",
      initiationId: initiationId ? +initiationId : -1,
      jobDescription: {
        jobTitle,
        currencyCode: salaryRange.currency,
        jobLocations:
          jobMode === "Remote"
            ? []
            : selectedJobLocations?.map((item) => item.name) || [],
        jobMode,
        maxExp: getIsFresher(experienceRange.min)
          ? "1 year"
          : `${experienceRange.max}`,
        minExp: `${experienceRange.min}`,
        maxSalary: salaryRange.max,
        minSalary: salaryRange.min,
        noticePeriod: noticePeriod || "",
        disabledFields,
        channelName:urlParams.get('cn')
      },
      recruiterDetail: null,
      selectedCount: +numberOfSelectedCandidates,
      sendMailToContactedProfiles: isSendToAlreadyContacted ? 1 : 0,
      status: "DRAFT",
      campaignId: getCampaignId(),
      whatsAppCountryCode: senderDetails?.countryCode,
      whatsAppName: senderDetails?.name,
      whatsAppNumber: senderDetails?.number,
      whatsAppNumberId: senderDetails?.id,
      templateId: templateDetails?.selectedTemplate?.id || null,
    };

    const formData = getFormData(payload);

    saveAsDraftApi({ payload: formData });
  }, [
    campaignName,
    selectedFolder?.folderId,
    selectedFolder?.name,
    initiationId,
    jobTitle,
    salaryRange.currency,
    salaryRange.max,
    salaryRange.min,
    jobMode,
    selectedJobLocations,
    experienceRange.max,
    experienceRange.min,
    noticePeriod,
    numberOfSelectedCandidates,
    isSendToAlreadyContacted,
    getCampaignId,
    senderDetails?.countryCode,
    senderDetails?.name,
    senderDetails?.number,
    senderDetails?.id,
    templateDetails?.selectedTemplate?.id,
    saveAsDraftApi,
  ]);

  useEffect(() => {
    // to handle save and continue
    if (isSaveAndContinueSuccess) {
      dispatch(
        whatsAppCampaignSliceActions.setSaveAndContinueResponse({
          response: saveAndContinueResponse || null,
        })
      );
      handleSaveAndContinueSuccess();

      // clever tap event on save and continue
      eventPushFunction({
        eventName: "Save and continue",
        data: {
          xcode: userDetails?.x_code || "",
          subuid: userDetails?.recruiter_id || "",
          channel_id: userDetails?.channel_id || "",
          subchannel_id: userDetails?.subhchannel_id || "",
          outreach_type: "WhatsApp",
          step: 1,
          campaign_id: saveAndContinueResponse?.id || "",
        },
      });
    }

    if (isSaveAndContinueError) {
      setToastValue({
        isToastShown: true,
        toastMessage: getErrorMsg({
          error: saveAndContinueError,
          defaultMsg: "Failed to save campaign. Please ty again.",
        }),
        toastKey: "saveAndContinueError",
        toastSeverity: "error",
      });
    }
  }, [
    dispatch,
    handleSaveAndContinueSuccess,
    isSaveAndContinueError,
    isSaveAndContinueSuccess,
    saveAndContinueError,
    saveAndContinueResponse,
    setToastValue,
    userDetails?.channel_id,
    userDetails?.recruiter_id,
    userDetails?.subhchannel_id,
    userDetails?.x_code,
  ]);

  useEffect(() => {
    // to handle saveAsDraft
    if (isSaveAsDraftSuccess) {
      // clever tap event on save as draft
      eventPushFunction({
        eventName: "Save as a draft",
        data: {
          xcode: userDetails?.x_code || "",
          subuid: userDetails?.recruiter_id || "",
          channel_id: userDetails?.channel_id || "",
          subchannel_id: userDetails?.subhchannel_id || "",
          outreach_type: "WhatsApp",
          step: 1,
          campaign_id: saveAndContinueResponse?.id || "",
        },
      });

      window.location.href = `${window.location.protocol}//${window.location.host}${MIDDLEWARE_SLUG}/listing`;
    }

    if (isSaveAsDraftError) {
      setToastValue({
        isToastShown: true,
        toastMessage: getErrorMsg({
          error: saveAdDraftError,
          defaultMsg: "Failed to save as draft. Please ty again.",
        }),
        toastKey: "saveAsDraftError",
        toastSeverity: "error",
      });
    }
  }, [
    isSaveAsDraftSuccess,
    isSaveAsDraftError,
    setToastValue,
    saveAdDraftError,
    saveAndContinueResponse?.id,
    userDetails?.x_code,
    userDetails?.recruiter_id,
    userDetails?.channel_id,
    userDetails?.subhchannel_id,
  ]);

  // useEffect(() => {
  //   // to update the default value of isSendToAlreadyContacted when trigger from folder and isBulkCampaign
  //   if (
  //     typeof isSendToAlreadyContacted !== "boolean" &&
  //     getIsTriggeredFromFolder(triggeredFromPage) &&
  //     isBulkCampaign
  //   ) {
  //     dispatch(
  //       campaignSetupActions.setIsSendToAlreadyContacted({
  //         isSendToAlreadyContacted: true,
  //       })
  //     );
  //   }
  // }, [dispatch, isBulkCampaign, isSendToAlreadyContacted, triggeredFromPage]);

  // useEffect(() => {
  //   // set default currency
  //   const siteContext = userData?.siteContext;
  //   if (siteContext && !salaryRange?.currency) {
  //     const defaultCurrency = getCurrencyBySiteContext("monstergulf");
  //     dispatch(
  //       campaignSetupActions.setSalaryRange({
  //         salaryRange: {
  //           currency: defaultCurrency || "INR",
  //           min: salaryRange?.min || "",
  //           max: salaryRange?.max || "",
  //         },
  //       })
  //     );
  //   }
  // }, [dispatch, salaryRange, userData?.siteContext]);

  return (
    <Accordion
      title="WhatsApp campaign setup"
      isEditMode={isEditMode}
      stepsCompleted={stepsCompleted}
      handleEditClick={handleEditClick}
      stepNumber={stepNumber}
      subTitle={subTitle}
    >
      <Box sx={{ my: 4 }}>
        <CampaignSetupForm
          jobTitleDetails={{
            handleUpdateJobTitle,
            jobTitle,
            error: errors.jobTitle || "",
          }}
          jobLocationDetails={{
            handleUpdateJobLocations,
            jobLocations,
            error: errors.jobLocations || "",
            selectedJobLocations,
          }}
          jobModeDetails={{ handleUpdateJobMode, jobMode }}
          experienceRangeDetails={{
            handleUpdateExperienceRange,
            experienceRange,
            error: errors.experienceRange || "",
          }}
          salaryRangeDetails={{
            handleUpdateSalaryRange,
            salaryRange,
            currencyList: currency || [],
            isSeaAndGulf: isSeaAndGulf(userData?.siteContext),
            error: errors.salaryRange || "",
            handleBlur,
          }}
          campaignNameDetails={{
            handleUpdateCampaignName,
            campaignName,
            error: errors?.campaignName || "",
          }}
          campaignPreferencesDetails={{
            handleUpdateIsSendToAlreadyContacted,
            isSendToAlreadyContacted: !!isSendToAlreadyContacted,
            isBulkCampaign,
            triggeredFromPage,
          }}
          numberOfSelectedCandidatesDetails={{
            handleUpdateNumberOfSelectedCandidates,
            numberOfSelectedCandidates,
            error: errors?.selectCandidates || "",
            isBulkCampaign,
            triggeredFromPage,
            campaignContactableDetails,
          }}
          selectedFolderDetails={{
            handleUpdateSelectedFolder,
            selectedFolder,
            error: errors?.saveInFolder || "",
            isBulkCampaign,
            triggeredFromPage,
            folderId:
              campaignContactableDetails.folderId ||
              draftDetails?.campaignDetail?.folderId ||
              null,
            folders,
            fetchFoldersStatus,
          }}
          noticePeriodDetails={{
            handleUpdateNoticePeriod,
            noticePeriod,
            error: "",
          }}
        />
      </Box>

      <Divider />
      <CampaignSetupButton
        stepNumber={stepNumber}
        faqData={faqDetails}
        handleSaveAndContinue={handleSaveAndContinue}
        handleSaveAsDraft={handleSaveAsDraft}
        isSaveAsDraftLoading={isSaveAsDraftLoading}
        isSaveAndContinueLoading={isSaveAndContinueLoading}
        isSaveAndContinueDisabled={!isAllFieldValid}
      />
    </Accordion>
  );
};

export default WhatsAppSetup;
